import React, { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { components } from "react-select";
import clx from "classnames";

import {
  CftLabel,
  CftFormControl,
  CftSelectField,
  CftTooltip,
  CftButton,
} from "../../components";
import useCftCattleValues from "../../utils/useCftCattleValues";

import "./CreateHerdField.scss";

export const cattleProductionGroups = [
  {
    value: 1,
    pl: "Cielę byk po urodzeniu – do 3 m-ca",
    gb: "Bull calf after birth - up to 3 months",
  },
  {
    value: 2,
    pl: "Cielę jałówka po urodzeniu – do 3 m-ca",
    gb: "Heifer calf after birth - up to 3 months",
  },
  {
    value: 3,
    pl: "Cielę rzeźne byk od 3 do 12 m- ca",
    gb: "A calf for slaughter from 3 to 12 months of age",
  },
  {
    value: 4,
    pl: "Cielę rzeźne jałówka od 3 do 12 m-ca",
    gb: "Heifer calf for slaughter from 3 to 12 months",
  },
  {
    value: 5,
    pl: "Jałówka odtworzeniowa od 12 do 24 m-ca",
    gb: "Reproductive heifer from 12 to 24 months",
  },
  {
    value: 6,
    pl: "Byk rzeźny od 12 do 24 m-ca",
    gb: "Slaughter bull from 12 to 24 months",
  },
  {
    value: 7,
    pl: "Krowa rzeźna od 12 do 24 m-ca",
    gb: "Slaughter cow from 12 to 24 months",
  },
  {
    value: 8,
    pl: "Krowa mamka od 12 do 24 m-ca",
    gb: "A suckler cow from 12 to 24 months",
  },
  {
    value: 9,
    pl: "Buhaj od 12 do 24 m-ca",
    gb: "Bull from 12 to 24 months",
  },
  {
    value: 10,
    pl: "Bydło po 24 m-cu",
    gb: "Cattle after 24 months",
  },
];

const CustomMenuWrapper = props => {
  const { t } = useTranslation(["cftTurkey", "cftChickenBroiler", "cftPigs"]);

  return (
    <components.Menu
      {...props}
      maxMenuHeight={props.maxMenuHeight - 50}
      minMenuHeight={props.minMenuHeight + 50}
    >
      {props.children}{" "}
      <CftButton
        isFullwidth
        isActive={!!props.selectProps.selectedProductionGroups.length}
        onClick={() => {
          props.selectProps.onSaveSelected();
          props.selectProps.onMenuClose();
        }}
        disabled={!props.selectProps.selectedProductionGroups.length}
      >
        {t("cftPigs:pigsHerdFeedsForm.customMenuBtn", "Zapisz wybór")}
      </CftButton>
    </components.Menu>
  );
};

const CustomOption = ({ innerRef, innerProps, children, ...props }) => {
  return (
    <div
      className="create-herd-field__custom-option"
      ref={innerRef}
      {...innerProps}
      {...props}
    >
      <span
        className={clx({
          "is-active": props.selectProps.selectedProductionGroups
            .map(({ production_group_type }) => production_group_type)
            .includes(props.value),
        })}
      />
      {children}
    </div>
  );
};

const CustomMenu = ({ innerRef, innerProps, children, ...props }) => {
  return (
    <>
      <components.MenuList
        className="create-herd-field__custom-menu"
        ref={innerRef}
        {...innerProps}
        {...props}
        maxHeight={props.maxHeight - 50}
      >
        {children}
      </components.MenuList>
    </>
  );
};

export default function CreateHerdField() {
  const { t, i18n } = useTranslation([
    "cftTurkey",
    "cftChickenBroiler",
    "cftPigs",
  ]);
  const { updateStore } = useCftCattleValues();
  const { setFieldValue, values } = useFormikContext();

  const productionGroupsTypesOptions = useMemo(() => {
    const productionGroupTypes = values.production_groups.map(
      ({ production_group_type }) => production_group_type
    );

    return cattleProductionGroups
      .filter(({ value }) => !productionGroupTypes.includes(value))
      .map(group => ({
        label: group[i18n.language],
        value: group.value,
      }));
  }, [cattleProductionGroups, values]);

  useEffect(() => {
    setFieldValue("production_groups_tmp", []);
  }, []);

  if (productionGroupsTypesOptions.length) {
    return (
      <>
        <div className="cft-paper mb-6">
          <CftFormControl alignLeft>
            <CftLabel
              isFullWidth
              label={t("cftPigs:pigsHerdFeedsForm.select", "Grupa produkcyjna")}
              className="mb-4"
            >
              <CftTooltip
                content={t(
                  "cftPigs:tooltips.productionGroupType",
                  "Dodaj tyle grup produkcyjnych, które występują w Twoim stadzie"
                )}
              />
            </CftLabel>
            <CftSelectField
              blurInputOnSelect={false}
              onSaveSelected={() => {
                updateStore({
                  herd_reference: {
                    ...values,
                    production_groups: [
                      ...values.production_groups,
                      ...values.production_groups_tmp,
                    ],
                  },
                });
                setFieldValue("production_groups", [
                  ...values.production_groups,
                  ...values.production_groups_tmp,
                ]);
                setFieldValue("production_groups_tmp", []);
              }}
              selectedProductionGroups={values.production_groups_tmp || []}
              components={{
                Option: CustomOption,
                MenuList: CustomMenu,
                Menu: CustomMenuWrapper,
              }}
              closeMenuOnSelect={false}
              name="tmp"
              options={productionGroupsTypesOptions}
              onChange={productionGroupType => {
                if (
                  values.production_groups_tmp
                    .map(({ production_group_type }) => production_group_type)
                    .includes(productionGroupType)
                ) {
                  setFieldValue(
                    "production_groups_tmp",
                    values.production_groups_tmp.filter(
                      ({ production_group_type }) =>
                        production_group_type !== productionGroupType
                    )
                  );
                } else {
                  setFieldValue("production_groups_tmp", [
                    ...values.production_groups_tmp,
                    {
                      production_group_type: productionGroupType,
                      animals_quantity: "",
                      animal_weight: "",
                      rearing_period: "",
                      repetition: "",
                      grazing_days: "",
                      grazing_hours: "",
                      milk_production: "",
                      isValid: false,
                      feed_components: [],
                    },
                  ]);
                }

                setFieldValue("tmp", "");
              }}
              placeholder={t(
                "cftTurkey:transportFields.fields.transportPlaceholder",
                "Wybierz z listy"
              )}
              isRounded
              isAlternative
            />
          </CftFormControl>
        </div>
      </>
    );
  }

  return null;
}
